/* Record types — handover §14 step 9, the definitions half.
 *
 * §6.2: "This is what makes the system Simon's rather than the builder's." A
 * type defined here gets a working register at /records/{id} with no code, so
 * this screen is the one that decides whether that promise holds.
 *
 * Two rules it has to enforce, both from §6.2, and both invisible if it does
 * not:
 *
 *   - Four promotion targets, each used once. Two fields promoting to the same
 *     column means the second silently wins on save, and the record quietly
 *     disagrees with its own payload.
 *   - The first field is the title. It is the indexed Title column that every
 *     list view and every deep link reads, so reordering fields changes what a
 *     record is called — which is worth saying out loud rather than letting
 *     somebody discover it.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useOnline } from '../../hooks/useOnline';
import { usedPromotions } from '../../lib/promote';
import type { FieldType, IRecordField, IRecordType, PromotedColumn } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Drawer,
  EmptyState,
  LoadError,
  Panel,
  TableSkeleton,
} from '../ui';
import s from './screens.module.scss';

const FIELD_TYPES: FieldType[] = [
  'text',
  'textarea',
  'number',
  'money',
  'date',
  'select',
  'check',
  'url',
  'person',
  'supplier',
];

const PROMOTIONS: PromotedColumn[] = ['Depot', 'ExpiryDate', 'Status', 'OwnerLookup'];

/** Only these can carry each promotion sensibly. Offering ExpiryDate on a
    checkbox would produce an indexed column holding "true". */
const PROMOTABLE_BY: Record<PromotedColumn, FieldType[]> = {
  Depot: ['select', 'text'],
  ExpiryDate: ['date'],
  Status: ['select', 'text'],
  OwnerLookup: ['person', 'text'],
};

function blankType(order: number): IRecordType {
  return {
    id: '',
    title: '',
    icon: 'ti-file-text',
    description: '',
    enabled: true,
    sortOrder: order,
    fields: [{ id: 'f1', label: '', type: 'text', list: true, required: true }],
  };
}

export function RecordTypesScreen() {
  const svc = useServices();
  const online = useOnline();
  const qc = useQueryClient();

  const [editing, setEditing] = useState<IRecordType | null>(null);

  const types = useQuery({ queryKey: qk.recordTypes, queryFn: () => svc.recordTypes.types() });
  const records = useQuery({
    queryKey: qk.customRecords(),
    queryFn: () => svc.recordTypes.records(),
  });

  const save = useMutation({

    meta: { ok: 'Record type saved' },
    mutationFn: (type: IRecordType) => svc.recordTypes.saveType(type),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.recordTypes });
      void qc.invalidateQueries({ queryKey: qk.audit });
      setEditing(null);
    },
  });

  const remove = useMutation({

    meta: { ok: 'Record type deleted' },
    mutationFn: (id: string) => svc.recordTypes.removeType(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.recordTypes });
      void qc.invalidateQueries({ queryKey: qk.customRecords() });
      setEditing(null);
    },
  });

  const countOf = (typeId: string) =>
    (records.data ?? []).filter((r) => r.typeId === typeId).length;

  const used = useMemo(
    () => (editing ? usedPromotions(editing) : new Set<string>()),
    [editing],
  );

  function patchField(index: number, patch: Partial<IRecordField>) {
    setEditing((t) =>
      t
        ? { ...t, fields: t.fields.map((f, i) => (i === index ? { ...f, ...patch } : f)) }
        : t,
    );
  }

  function addField() {
    setEditing((t) =>
      t
        ? {
            ...t,
            fields: [
              ...t.fields,
              { id: `f${t.fields.length + 1}-${Date.now()}`, label: '', type: 'text' },
            ],
          }
        : t,
    );
  }

  function moveField(index: number, by: number) {
    setEditing((t) => {
      if (!t) return t;
      const to = index + by;
      if (to < 0 || to >= t.fields.length) return t;
      const fields = [...t.fields];
      const [moved] = fields.splice(index, 1);
      fields.splice(to, 0, moved);
      return { ...t, fields };
    });
  }

  const incomplete =
    editing !== null &&
    (editing.title.trim() === '' || editing.fields.some((f) => f.label.trim() === ''));

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Record types</h1>
          <p>
            Define your own registers. A type saved here gets a working list, form and expiry
            feed at once.
          </p>
        </div>
        <Button
          primary
          icon="ti-plus"
          disabled={!online}
          onClick={() => setEditing(blankType((types.data?.length ?? 0) + 1))}
        >
          New type
        </Button>
      </div>

      {types.isError && <LoadError error={types.error} what="record types" />}

      {types.isLoading ? (
        <Panel flush>
          <TableSkeleton rows={4} />
        </Panel>
      ) : (types.data ?? []).length === 0 ? (
        <Panel flush>
          <EmptyState icon="ti-layout-grid-add" title="No record types yet">
            A record type is anything the built-in registers do not cover — support contracts,
            calibration certificates, insurance policies.
          </EmptyState>
        </Panel>
      ) : (
        <div className={s.stack}>
          {(types.data ?? []).map((t) => (
            <Panel
              key={t.id}
              title={t.title}
              sub={t.description}
              actions={
                <div className={s.tags}>
                  {!t.enabled && <Badge tone="muted">Disabled</Badge>}
                  <Badge tone="info">{countOf(t.id)} records</Badge>
                  <Link to={`/records/${t.id}`} className={s.panelMore}>
                    Open register
                  </Link>
                  <Button icon="ti-pencil" onClick={() => setEditing(t)} disabled={!online}>
                    Edit
                  </Button>
                </div>
              }
            >
              <div className={s.tags}>
                {t.fields.map((f) => (
                  <Badge key={f.id} tone={f.promote ? 'purple' : 'muted'} icon={f.list ? 'ti-table' : undefined}>
                    {f.label || 'Unnamed'} · {f.type}
                    {f.promote ? ` → ${f.promote}` : ''}
                  </Badge>
                ))}
              </div>
            </Panel>
          ))}
        </div>
      )}

      <Drawer
        open={editing !== null}
        title={editing?.id ? editing.title || 'Untitled type' : 'New record type'}
        sub="Fields, and what gets promoted to an indexed column"
        avatar={<i className={`ti ${editing?.icon ?? 'ti-file-text'}`} aria-hidden />}
        onClose={() => setEditing(null)}
      >
        {editing && (
          <>
            <div className={s.field}>
              <label htmlFor="type-title">Name</label>
              <input
                id="type-title"
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="Support contract"
              />
            </div>

            <div className={s.field}>
              <label htmlFor="type-desc">Description</label>
              <textarea
                id="type-desc"
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                placeholder="What this register is for, in a sentence."
              />
            </div>

            <div className={s.field}>
              <label htmlFor="type-icon">Icon</label>
              <input
                id="type-icon"
                value={editing.icon}
                onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                placeholder="ti-file-certificate"
              />
              <div className={s.hint}>
                A Tabler icon name. <i className={`ti ${editing.icon}`} aria-hidden /> is what{' '}
                <span className={s.mono}>{editing.icon}</span> looks like.
              </div>
            </div>

            <div className={s.field}>
              <label htmlFor="type-enabled" className={s.checkRow}>
                <input
                  id="type-enabled"
                  type="checkbox"
                  checked={editing.enabled}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                />
                <span>Enabled</span>
              </label>
            </div>

            <div>
              <div className={s.sectionLabel}>Fields</div>
              <div className={s.hint}>
                The first field is the record's title — it is the indexed column every list and
                every link reads, so moving a field to the top renames every record of this type.
              </div>

 <div className={`${s.stack} ${`${s.rowGap}`} ${s.spaced}`}>
                {editing.fields.map((f, i) => (
                  <div key={f.id} className={s.fieldRow}>
                    <div className={s.fieldRowHead}>
                      <Badge tone={i === 0 ? 'info' : 'muted'}>{i === 0 ? 'Title' : `#${i + 1}`}</Badge>
                      <div className={s.grow} />
                      <button
                        type="button"
                        className={s.iconOnly}
                        onClick={() => moveField(i, -1)}
                        disabled={i === 0}
                        aria-label="Move up"
                      >
                        <i className="ti ti-arrow-up" aria-hidden />
                      </button>
                      <button
                        type="button"
                        className={s.iconOnly}
                        onClick={() => moveField(i, 1)}
                        disabled={i === editing.fields.length - 1}
                        aria-label="Move down"
                      >
                        <i className="ti ti-arrow-down" aria-hidden />
                      </button>
                      <button
                        type="button"
                        className={s.iconOnly}
                        onClick={() =>
                          setEditing({
                            ...editing,
                            fields: editing.fields.filter((_, x) => x !== i),
                          })
                        }
                        disabled={editing.fields.length === 1}
                        aria-label="Remove field"
                      >
                        <i className="ti ti-trash" aria-hidden />
                      </button>
                    </div>

                    <div className={s.fieldRowBody}>
                      <input
                        value={f.label}
                        onChange={(e) => patchField(i, { label: e.target.value })}
                        placeholder="Label"
                        aria-label={`Field ${i + 1} label`}
                      />
                      <select
                        value={f.type}
                        onChange={(e) => {
                          const type = e.target.value as FieldType;
                          /* A promotion that no longer fits the new type is
                             dropped rather than left pointing at a column it
                             cannot fill. */
                          const keep = f.promote && PROMOTABLE_BY[f.promote].includes(type);
                          patchField(i, { type, promote: keep ? f.promote : undefined });
                        }}
                        aria-label={`Field ${i + 1} type`}
                      >
                        {FIELD_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      <select
                        value={f.promote ?? ''}
                        onChange={(e) =>
                          patchField(i, {
                            promote: (e.target.value || undefined) as PromotedColumn | undefined,
                            // Promoting a date is what feeds the expiry aggregation.
                            expiry: e.target.value === 'ExpiryDate' ? true : f.expiry,
                          })
                        }
                        aria-label={`Field ${i + 1} promotion`}
                      >
                        <option value="">Not promoted</option>
                        {PROMOTIONS.filter(
                          (p) =>
                            PROMOTABLE_BY[p].includes(f.type) && (!used.has(p) || f.promote === p),
                        ).map((p) => (
                          <option key={p} value={p}>
                            → {p}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className={s.fieldRowBody}>
                      <label className={s.checkRow}>
                        <input
                          type="checkbox"
                          checked={Boolean(f.list)}
                          onChange={(e) => patchField(i, { list: e.target.checked })}
                        />
                        <span>Show in the list</span>
                      </label>
                      <label className={s.checkRow}>
                        <input
                          type="checkbox"
                          checked={Boolean(f.required)}
                          onChange={(e) => patchField(i, { required: e.target.checked })}
                        />
                        <span>Required</span>
                      </label>
                      {f.type === 'select' && (
                        <input
                          value={(f.options ?? []).join(', ')}
                          onChange={(e) =>
                            patchField(i, {
                              options: e.target.value
                                .split(',')
                                .map((o) => o.trim())
                                .filter(Boolean),
                            })
                          }
                          placeholder="Options, comma separated"
                          aria-label={`Field ${i + 1} options`}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className={s.spaced}>
                <Button icon="ti-plus" onClick={addField}>
                  Add field
                </Button>
              </div>
            </div>

            <div className={s.ownedNote}>
              <i className="ti ti-database-cog" aria-hidden />
              <span>
                {used.size} of {PROMOTIONS.length} promoted columns used. A promoted field is
                copied into a real indexed column on save, which is what lets "everything expiring
                in 90 days" span every type in one query. Four, deliberately — each one is a value
                kept in two places.
              </span>
            </div>

            <div className={s.actions}>
              <Button
                primary
                icon="ti-device-floppy"
                disabled={!online || save.isPending || incomplete}
                onClick={() => save.mutate(editing)}
              >
                {save.isPending ? 'Saving…' : 'Save type'}
              </Button>
              {editing.id && (
                <Button
                  icon="ti-trash"
                  disabled={!online || remove.isPending}
                  onClick={() => {
                    const n = countOf(editing.id);
                    const warning = n
                      ? `Delete "${editing.title}" and its ${n} record${n === 1 ? '' : 's'}? This cannot be undone.`
                      : `Delete "${editing.title}"?`;
                    if (window.confirm(warning)) remove.mutate(editing.id);
                  }}
                >
                  Delete type
                </Button>
              )}
            </div>

            {incomplete && (
              <div className={s.hint}>A type needs a name, and every field needs a label.</div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
